import { Component } from "react"
import PropTypes from "prop-types"
import { map, get, chain, isEmpty } from "lodash"
import { Chip } from "@mui/material"
import { PERIOD } from "helpers/shapes"
import Stepper from "components/stepper"
import SaveExperiment from "modules/save-experiment"
import SmartTradeSetup from "./components/smart-trade-setup"
import SmartTradeUniverse from "./components/smart-trade-universe"
import SmartTradeFinalize from "./components/smart-trade-finalize"
import { makeStepAnalytics, BASKET_TYPES } from "./helpers"
import HelpMenu from "modules/help-menu"

function stepRouting(step, navigate, account, researchTopicId, experiment) {
  let prefix = `/${account}/t/${researchTopicId}/smart-trade/`
  if (experiment) {
    prefix += `${experiment.id}/edit`
  } else {
    prefix += "create"
  }

  switch (step) {
    case 0:
      navigate(`${prefix}/setup`)
      break
    case 1:
      navigate(`${prefix}/universe`)
      break
    case 2:
      navigate(`${prefix}/finalize`)
      break
  }
}

function openCancelDialog(navigate, editedExperiment, openDialog, account, researchTopicId, resetSecurityFinderFacets, resetSecurityFinderFilters) {
  const cancelAction = () => {
    resetSecurityFinderFilters()
    resetSecurityFinderFacets()
    navigate(`/${account}/t/${researchTopicId}/experiments`, { replace: true })
  }

  if (editedExperiment) {
    return openDialog({
      title: "Cancel Modifications",
      contentText: `Any edits made to ${editedExperiment.name} will be lost. Are you sure you want to continue?`,
      cancelText: "Cancel",
      buttons: [{
        text: "Confirm",
        type: "primary",
        action: cancelAction,
      }],
    })
  }

  return openDialog({
    title: "Would you like to cancel this smart trade ?",
    contentText: "Canceling will clear all options and delete any data provided",
    cancelText: "Return To Smart Trade",
    buttons: [{
      text: "Cancel Smart Trade",
      type: "primary",
      action: cancelAction,
    }],
  })
}

export default class SmartTrade extends Component {
  static propTypes = {
    params: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    account: PropTypes.string.isRequired,
    step: PropTypes.number.isRequired,
    activeStep: PropTypes.number.isRequired,
    completedStep: PropTypes.number,
    openDialog: PropTypes.func.isRequired,
    steps: PropTypes.array.isRequired,
    setHeader: PropTypes.func.isRequired,
    researchTopicId: PropTypes.string.isRequired,
    referenceInstrument: PropTypes.object.isRequired,
    submitStepSuccess: PropTypes.func.isRequired,
    setModel: PropTypes.func.isRequired,
    modelId: PropTypes.string.isRequired,
    models: PropTypes.array.isRequired,
    instrumentDescriptors: PropTypes.object,
    setupTrade: PropTypes.func.isRequired,
    instrumentLatestDate: PropTypes.string.isRequired,
    instrumentEarliestDate: PropTypes.string.isRequired,
    filter: PropTypes.object,
    securitiesCount: PropTypes.number,
    resetSecurityFinderFilters: PropTypes.func.isRequired,
    resetSecurityFinderFacets: PropTypes.func.isRequired,
    loadSecurityFinderFilters: PropTypes.func.isRequired,
    instrumentSecurityFilter: PropTypes.object.isRequired,
    chipsInfo: PropTypes.array.isRequired,
    comparisonItems: PropTypes.array,
    comparisonTypes: PropTypes.array.isRequired,
    selectedComparisonType: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    setSelectedComparisonType: PropTypes.func.isRequired,
    selectedDate: PropTypes.string.isRequired,
    tradeDate: PropTypes.string,
    period: PERIOD.isRequired,
    latestDate: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    setPeriod: PropTypes.func.isRequired,
    resetSelectedDate: PropTypes.func.isRequired,
    baseUrl: PropTypes.string.isRequired,
    setScorecardDate: PropTypes.func.isRequired,
    scorecardDate: PropTypes.string.isRequired,
    trackPanelTrigger: PropTypes.func.isRequired,
    trackSmartTradeStepper: PropTypes.func.isRequired,
    setSelectedDate: PropTypes.func.isRequired,
    displayTradeSize: PropTypes.number,
    contentSets: PropTypes.array.isRequired,
    basketRules: PropTypes.object.isRequired,
    smartTradeRules: PropTypes.array,
    optimizationScanner: PropTypes.func.isRequired,
    scorecardHeadline: PropTypes.object,
    status: PropTypes.string.isRequired,
    isSingleDate: PropTypes.bool.isRequired,
    experiment: PropTypes.object,
    smartTradePositionSet: PropTypes.object,
    notifyUniverseSecurities: PropTypes.func.isRequired,
    smartTradeRefEquities: PropTypes.array,
    editedExperiment: PropTypes.object,
    loadSmartTrade: PropTypes.func,
    loadSmartTradeSearchRules: PropTypes.func,
    resetSmartTradesRulesSearch: PropTypes.func,
    tradeSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // When no value is entered, we send empty string
    segmentNormalization: PropTypes.object,
    unmodeledDates: PropTypes.object.isRequired,
    instrumentPortfolio: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    instrumentPortfolioPositions: PropTypes.object,
    instrumentPortfolioGMV: PropTypes.number,
    longOrShortInstrument: PropTypes.oneOf(BASKET_TYPES),
    addNotification: PropTypes.func.isRequired,
    modelHasDates: PropTypes.bool.isRequired,
    errors: PropTypes.object,
  }

  static getHeaderConfig({ referenceInstrument, editedExperiment, openDialog, navigate, account, resetSecurityFinderFacets, researchTopicId, models, modelId, instrumentDescriptors, instrumentPortfolio, resetSecurityFinderFilters }) {
    let title = "Loading..."
    let name = ""
    let type = "trade"
    if (instrumentDescriptors) {
      name = instrumentDescriptors.ticker
    }
    if (!isEmpty(instrumentPortfolio)) {
      name = instrumentPortfolio.name
      type = "portfolio"
    }

    if (editedExperiment) {
      title = `Update smart ${type} : ${editedExperiment.name}`
    } else if (name) {
      title = `Smart ${type} for ${name}`
    }
    const modelName = chain(models).find(({ id }) => id === modelId).get("name", modelId).value()
    return {
      title,
      key: `smartTrade${researchTopicId}-${JSON.stringify(instrumentDescriptors || instrumentPortfolio)}-${modelId}`,
      subnav: modelId && referenceInstrument ? <div className="fake-model-selector">{modelName}</div> : <div />,
      actions: {
        home: () => openCancelDialog(navigate, editedExperiment, openDialog, account, researchTopicId, resetSecurityFinderFacets, resetSecurityFinderFilters),
        help: <HelpMenu modelId={modelId} iconButton />,
      },
    }
  }

  isDateUnavailable = () => {
    const { instrumentLatestDate, instrumentEarliestDate, tradeDate, unmodeledDates, modelHasDates } = this.props
    // If for some reason you are already on a model in which the instrument is unmodeled
    if (!modelHasDates) { return true }
    return tradeDate && (tradeDate > instrumentLatestDate || tradeDate < instrumentEarliestDate || unmodeledDates[tradeDate])
  }

  componentDidMount() {
    const { navigate, loadSmartTrade, resetSmartTradesRulesSearch, loadSmartTradeSearchRules, editedExperiment, step, account, researchTopicId, resetSecurityFinderFacets, resetSecurityFinderFilters, loadSecurityFinderFilters, instrumentSecurityFilter, addNotification, setupTrade, tradeDate } = this.props

    if (step !== 0 && !editedExperiment) {
      setTimeout(() => navigate(`/${account}/t/${researchTopicId}/smart-trade/create/setup`, { replace: true }))
    } else {
      if (this.isDateUnavailable()) {
        addNotification("error", null, 'This trade date is unavailable with the selected risk model"')
      } else {
        // The tradeDate cannot be changed and will never trigger the onChange(setupTrade) to update the value
        // Because this value cannot be changed (comes from RT as prop), we manually update the state
        setupTrade({ tradeDate })
      }

      resetSecurityFinderFilters()
      resetSecurityFinderFacets()
      resetSmartTradesRulesSearch()

      if (!editedExperiment) {
        loadSecurityFinderFilters(instrumentSecurityFilter)
      } else {
        loadSecurityFinderFilters(get(editedExperiment, "config.filterConfig.filters"))
        loadSmartTrade(editedExperiment.config, editedExperiment.modelId)
        const rules = get(editedExperiment, "config.smartTradeRules")
        if (rules) {
          loadSmartTradeSearchRules(rules)
        }
      }
    }
  }

  componentDidUpdate() {
    const { step, activeStep, submitStepSuccess } = this.props
    if (step !== activeStep) {
      submitStepSuccess(step)
    }
  }

  toggleSaveExperiment = () => {
    const { openDialog, isSingleDate, experiment, researchTopicId, editedExperiment } = this.props

    if (editedExperiment) {
      const selectedExperiment = { ...editedExperiment, ...experiment, positionSet: get(experiment, "positionSet.dates.0") }

      openDialog({
        title: `Update Experiment: ${editedExperiment.name}`,
        content: <SaveExperiment experiment={selectedExperiment} researchTopicId={researchTopicId} isOneDayExperiment={isSingleDate} isAdvanceEditMode />,
      })
    } else {
      openDialog({
        title: "Create Experiment",
        content: <SaveExperiment experiment={experiment} researchTopicId={researchTopicId} submitLabel="Save Experiment" isOneDayExperiment={isSingleDate} />,
      })
    }
  }

  onGoToStep = (selectedStep) => {
    const { navigate, submitStepSuccess, account, researchTopicId, editedExperiment } = this.props
    submitStepSuccess(selectedStep)
    stepRouting(selectedStep, navigate, account, researchTopicId, editedExperiment)
  }

  onSubmit = () => {
    const { activeStep, navigate, submitStepSuccess, account, researchTopicId, optimizationScanner, editedExperiment } = this.props
    const nextStep = activeStep + 1

    if (nextStep < 3) {
      submitStepSuccess(nextStep)
      if (nextStep === 2) {
        optimizationScanner()
      }

      stepRouting(nextStep, navigate, account, researchTopicId, editedExperiment)
    } else {
      this.toggleSaveExperiment()
    }
  }

  onModelChange = (modelId) => {
    const { completedStep, setModel } = this.props
    if (completedStep > 0) {
      this.props.openDialog({
        title: "Confirm Model Change",
        contentText: "Changing the model will reset your filters and rules.",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => setModel(modelId),
        }],
      })
    } else {
      setModel(modelId)
    }
  }

  onCancel = () => {
    const { navigate, account, editedExperiment, researchTopicId, openDialog, resetSecurityFinderFilters, resetSecurityFinderFacets } = this.props
    openCancelDialog(navigate, editedExperiment, openDialog, account, researchTopicId, resetSecurityFinderFacets, resetSecurityFinderFilters)
  }

  render() {
    const {
      activeStep,
      steps,
      instrumentDescriptors,
      setupTrade,
      instrumentEarliestDate,
      instrumentLatestDate,
      modelId,
      filter,
      securitiesCount,
      referenceInstrument,
      chipsInfo,
      comparisonItems,
      comparisonTypes,
      account,
      setSelectedComparisonType,
      selectedComparisonType,
      selectedDate,
      tradeDate,
      period,
      setPeriod,
      latestDate,
      resetSelectedDate,
      baseUrl,
      setScorecardDate,
      scorecardDate,
      startDate,
      endDate,
      trackPanelTrigger,
      setSelectedDate,
      displayTradeSize,
      contentSets,
      basketRules,
      smartTradeRules,
      status,
      isSingleDate,
      scorecardHeadline,
      trackSmartTradeStepper,
      step,
      smartTradePositionSet,
      models,
      notifyUniverseSecurities,
      smartTradeRefEquities,
      editedExperiment,
      tradeSize,
      segmentNormalization,
      unmodeledDates,
      instrumentPortfolio,
      instrumentPortfolioPositions,
      instrumentPortfolioGMV,
      completedStep,
      longOrShortInstrument,
      errors,
    } = this.props

    const basketSize = Math.abs(basketRules.basketSize)
    const basketType = longOrShortInstrument === "SHORT_ONLY" ? "long" : "short"

    let currentStep
    let subHeaderComponent
    switch (activeStep) {
      case 0:
        currentStep = <SmartTradeSetup
          instrumentDescriptors={instrumentDescriptors}
          tradeDate={tradeDate}
          tradeSize={displayTradeSize}
          basketType={basketType}
          basketSize={basketSize}
          onChange={setupTrade}
          instrumentEarliestDate={instrumentEarliestDate}
          instrumentLatestDate={instrumentLatestDate}
          referenceInstrument={referenceInstrument}
          instrumentPortfolio={instrumentPortfolio}
          instrumentPortfolioGMV={instrumentPortfolioGMV}
          modelId={modelId}
          models={models}
          setModel={this.onModelChange}
          unmodeledDates={unmodeledDates}
          completedStep={completedStep}
          longOrShortInstrument={longOrShortInstrument}
        />
        break
      case 1:
        currentStep = <SmartTradeUniverse
          modelId={modelId}
          selectedDate={tradeDate}
          instrumentPortfolio={instrumentPortfolio || undefined}
        />
        break
      case 2:
        currentStep = <SmartTradeFinalize
          account={account}
          modelId={modelId}
          comparisonItems={comparisonItems}
          comparisonTypes={comparisonTypes}
          selectedDate={selectedDate}
          selectedComparisonType={selectedComparisonType}
          setSelectedComparisonType={setSelectedComparisonType}
          basketStartDate={tradeDate}
          period={period}
          latestDate={latestDate}
          setPeriod={setPeriod}
          resetSelectedDate={resetSelectedDate}
          baseUrl={baseUrl}
          setScorecardDate={setScorecardDate}
          scorecardDate={scorecardDate}
          startDate={startDate}
          endDate={endDate}
          trackPanelTrigger={trackPanelTrigger}
          setSelectedDate={setSelectedDate}
          contentSets={contentSets}
          instrumentDescriptors={instrumentDescriptors}
          basketRules={basketRules}
          smartTradeRules={smartTradeRules}
          isSingleDate={isSingleDate}
          scorecardHeadline={scorecardHeadline}
          smartTradePositionSet={smartTradePositionSet}
          securitiesCount={securitiesCount}
          notifyUniverseSecurities={notifyUniverseSecurities}
          tradeSize={tradeSize}
          segmentNormalization={segmentNormalization}
          instrumentPortfolio={instrumentPortfolio}
          instrumentPortfolioPositions={instrumentPortfolioPositions}
          smartTradeRefEquities={smartTradeRefEquities}
        />
        break
      default:
        break
    }

    if (chipsInfo.length > 0) {
      subHeaderComponent = <div className="smart-trade-stepper-subheader">
        {map(chipsInfo, (chip, index) => <Chip
          key={index}
          label={chip.label}
          className="smart-trade-setup-chip"
          clickable
          color="primary"
          variant="outlined"
          onClick={() => this.onGoToStep(chip.gotoStep)}
          classes={{ label: "chip-content" }}
        />)}
      </div>
    }

    return <div className="modules_smart-trade">
      <Stepper
        className="test-stepper"
        goToStep={this.onGoToStep}
        steps={steps}
        activeStep={activeStep}
        instrumentType={referenceInstrument.type}
        disableVariables={{ tradeDate, status, tradeSize: displayTradeSize, basketSize, filter, securitiesCount, instrumentType: referenceInstrument.type, isDateUnavailable: this.isDateUnavailable(), basketSizeErrors: get(errors, "basketSize") }}
        onSubmit={this.onSubmit}
        onCancel={this.onCancel}
        stepComponent={currentStep}
        subHeaderComponent={subHeaderComponent}
        track={(action) => {
          const analytics = makeStepAnalytics(action, step, !!editedExperiment)
          trackSmartTradeStepper(analytics.action, analytics.step)
        }}
      />
    </div>
  }
}
